/**
 * "Open share by name…" (plan §3.2, Task 7.4b): an Entra identity with a Storage File Data role reads the files inside
 * a share but cannot list the account's shares, so it names the share. Main verifies the share root with the
 * connection's own sign-in before the name joins the connection's named shares; a refusal (the missing data role, a
 * share that does not exist) stays inline and nothing is saved. TanStack Form; the share-name rule runs live; one
 * submit in flight (pending latch). Opened by the File Shares folder's action row and menu (Task 7.13).
 */
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { shareNameProblem } from '../../../../../core/engines/azureStorageFilePaths';
import { openShareByName, type ShareActionSource } from '../namedShareActions';

type Props = { open: boolean; connectionId: string; source: ShareActionSource; onClose: () => void };

export function OpenShareByNameDialog({ open, connectionId, source, onClose }: Props) {
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const form = useForm({
    defaultValues: { share: '' },
    onSubmit: async ({ value }) => {
      const share = value.share.trim();
      if (pending || shareNameProblem(share)) return;
      setPending(true);
      setError('');
      const result = await openShareByName(connectionId, share, source);
      setPending(false);
      if (!result.ok) { setError(result.error.message); return; }
      onClose();
    },
  });
  useEffect(() => { if (open) { form.reset(); setError(''); setPending(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !pending) onClose(); }}>
      <DialogContent data-testid="open-share-by-name-dialog">
        <DialogHeader>
          <DialogTitle>Open share by name</DialogTitle>
          <DialogDescription>
            Jam SQL checks that this sign-in can read the share, then adds it to the connection.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); void form.handleSubmit(); }}>
          <form.Field name="share">
            {(field) => {
              const problem = field.state.value.trim() ? shareNameProblem(field.state.value.trim()) : undefined;
              return (
                <div>
                  <Label htmlFor="open-share-by-name-input">Share name</Label>
                  <Input spellCheck={false} autoComplete="off" id="open-share-by-name-input" value={field.state.value} onChange={(event) => field.handleChange(event.target.value)} autoFocus data-testid="open-share-by-name-input" />
                  {problem && <p className="mt-1 text-xs text-destructive" data-testid="open-share-by-name-problem">{problem}</p>}
                </div>
              );
            }}
          </form.Field>
          {error && <p className="text-sm text-destructive" data-testid="open-share-by-name-error">{error}</p>}
          <form.Subscribe selector={(state) => state.values.share}>
            {(share) => (
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={pending}>Cancel</Button>
                <Button type="submit" disabled={pending || !share.trim() || Boolean(shareNameProblem(share.trim()))} data-testid="open-share-by-name-submit">
                  {pending && <RandomDotmSpinner size={14} className="mr-1" />}Open share
                </Button>
              </div>
            )}
          </form.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  );
}
